/**
 * ImageRatingInput Component
 *
 * Interactive star rating input for gallery images.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ImageRatingInputProps {
  /** Current rating value (1-5) or null if not rated */
  value: number | null;
  /** Callback when rating changes */
  onChange: (rating: number) => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CLASSES = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

export function ImageRatingInput({
  value,
  onChange,
  disabled = false,
  className,
  size = 'md',
}: ImageRatingInputProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  const displayValue = hovered ?? value ?? 0;

  return (
    <div
      className={cn('flex items-center gap-0.5', className)}
      role="radiogroup"
      aria-label="Bewertung abgeben"
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const isActive = star <= displayValue;

        return (
          <button
            key={star}
            type="button"
            onClick={() => !disabled && onChange(star)}
            onMouseEnter={() => !disabled && setHovered(star)}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => !disabled && setHovered(star)}
            onBlur={() => setHovered(null)}
            disabled={disabled}
            className={cn(
              'p-0.5 rounded transition-transform',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
              !disabled && 'hover:scale-110 cursor-pointer',
              disabled && 'cursor-not-allowed opacity-50'
            )}
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} ${star === 1 ? 'Stern' : 'Sterne'}`}
          >
            <Star
              className={cn(
                SIZE_CLASSES[size],
                'transition-colors',
                isActive
                  ? 'fill-amber-400 text-amber-400'
                  : 'fill-transparent text-neutral-300 dark:text-neutral-600'
              )}
            />
          </button>
        );
      })}
    </div>
  );
}
